import { GetSimpleCrateDto } from '@cratehackers/api-client';
import { AppIdentifiers } from '../store/slices/token-state.slice';
import { getAppStore } from '../store/storeRef';
import { iCrate } from '../types/ICrate';
import { iTrack } from '../types/ITrack';
import ApiService from './Api.service';
import {
  hydrateCrateTracksMetadata,
  resolveAgentTracksForCrate,
  type AgentCrateTrackInput,
} from './AgentCrateTracks.service';
import { CrateService } from './Crate.service';
import { formatMusicalKey } from '../utils/formatMusicalKey';
import { ensureTrackIds } from '../utils/ensureTrackId';
import { findBestMixableSequence } from '../utils/magicSort';
import { readTrackArtist, readTrackTitle } from '../utils/normalizeCrateTrack';

type CrateRow = GetSimpleCrateDto & {
  _id?: string;
  Title?: string;
  Type?: string;
  Hacker?: unknown;
  Category?: unknown;
};

type CrateSummary = {
  id: string;
  title: string;
  trackCount: number | null;
  categories: string[];
  hackers: string[];
  type: string | null;
  order: 'bpm-and-key';
};

const loggedOut = () => ({
  available: false,
  message:
    'Sign in to Crate Hackers to see the catalog, your crates, and example crates. Those crates are in BPM and key order.',
});

const isSignedIn = (): boolean =>
  !!getAppStore().getState().tokenState[AppIdentifiers.App]?.accessToken;

const hackerNames = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((hacker: Record<string, unknown>) => {
      if (!hacker || typeof hacker !== 'object') return '';
      const name =
        (typeof hacker.name === 'string' && hacker.name) ||
        `${hacker.firstName ?? hacker.First_Name ?? ''} ${
          hacker.lastName ?? hacker.Last_Name ?? ''
        }`.trim();
      return name.replace(/\s+/g, ' ').trim();
    })
    .filter(Boolean);
};

const categoryNames = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (!item || typeof item !== 'object') return '';
      const row = item as { name?: string; Name?: string };
      return String(row.name ?? row.Name ?? '').trim();
    })
    .filter(Boolean);
};

const summarizeCrate = (crate: CrateRow): CrateSummary => ({
  id: crate.id || crate._id || '',
  title: crate.title || crate.Title || 'Untitled crate',
  trackCount: crate.trackCount ?? null,
  categories: categoryNames(crate.category ?? crate.Category),
  hackers: hackerNames(crate.hacker ?? crate.Hacker),
  type: crate.type || crate.Type || null,
  order: 'bpm-and-key',
});

const summarizeCatalogTrack = (track: iTrack, index: number) => ({
  position: index + 1,
  id: track.ID ?? track.SpotifyID ?? null,
  artist: readTrackArtist(track) || null,
  title: readTrackTitle(track) || null,
  bpm: track.BPM ?? null,
  key: formatMusicalKey(track.Key_Camelot ?? track.Key) || null,
  albumCover: track.AlbumCover ?? null,
  energy: track.energy ?? null,
  danceability: track.danceability ?? null,
  valence: track.valence ?? null,
});

const cratesFromJson = (data: unknown): CrateRow[] => {
  if (!data || typeof data !== 'object') return [];
  const results = (data as { results?: unknown }).results;
  return Array.isArray(results) ? (results as CrateRow[]) : [];
};

const cratesFromRaw = async (
  request: () => Promise<{ raw: Response }>,
): Promise<CrateRow[]> => {
  const response = await request();
  if (!response.raw.ok) {
    throw new Error(`Crate request failed (${response.raw.status})`);
  }
  return cratesFromJson(await response.raw.json());
};

const matchesQuery = (crate: CrateSummary, query: string) => {
  if (!query) return true;
  const hay = [crate.title, crate.type, ...crate.categories, ...crate.hackers]
    .join(' ')
    .toLowerCase();
  return hay.includes(query.toLowerCase());
};

const collection = (label: string, kind: string, crates: CrateSummary[]) => ({
  available: true,
  label,
  kind,
  count: crates.length,
  crates,
});

const failedCollection = (label: string, kind: string, error: unknown) => ({
  available: false,
  label,
  kind,
  count: 0,
  crates: [] as CrateSummary[],
  message: error instanceof Error ? error.message : `Could not load ${label}.`,
});

export const searchCrateHackersCrates = async (query?: string) => {
  if (!isSignedIn()) return loggedOut();

  const term = query?.trim() ?? '';
  const api = ApiService.getApiClient();

  const [catalog, mine, examples] = await Promise.all([
    (async () => {
      try {
        const data = term
          ? await api.crateControllerSearch({
              crateSearchDto: { term, offset: 0 },
            })
          : await api.crateControllerFindCuratedCrates({
              cursor: 0,
              archived: 'false',
            });
        const crates = (data?.results ?? [])
          .map((crate) => summarizeCrate(crate as CrateRow))
          .filter((crate) => crate.id)
          .slice(0, 20);
        return collection(
          term
            ? 'Crate Hackers catalog matches'
            : 'Crate Hackers curated crates',
          'catalog',
          crates,
        );
      } catch (error) {
        return failedCollection('Crate Hackers catalog', 'catalog', error);
      }
    })(),
    (async () => {
      try {
        const crates = (
          await cratesFromRaw(() =>
            api.crateControllerFindMyCratesWithGroupsRaw(),
          )
        )
          .map(summarizeCrate)
          .filter((crate) => crate.id && matchesQuery(crate, term))
          .slice(0, 20);
        return collection("This DJ's Crate Hackers crates", 'mine', crates);
      } catch (error) {
        return failedCollection(
          "This DJ's Crate Hackers crates",
          'mine',
          error,
        );
      }
    })(),
    (async () => {
      try {
        const crates = (
          await cratesFromRaw(() => api.crateControllerFindStandardRaw())
        )
          .map(summarizeCrate)
          .filter((crate) => crate.id && matchesQuery(crate, term))
          .slice(0, 20);
        return collection('Crate Hackers example crates', 'examples', crates);
      } catch (error) {
        return failedCollection(
          'Crate Hackers example crates',
          'examples',
          error,
        );
      }
    })(),
  ]);

  return {
    available: true,
    world: 'crate-hackers',
    order: 'bpm-and-key',
    query: term || null,
    note: "These are Crate Hackers crates — catalog, this DJ's, and examples. Tracks are already in BPM and key order. Serato and VirtualDJ are separate optional DJ apps, not this list.",
    catalog,
    mine,
    examples,
  };
};

export const getCrateHackersCrate = async (crateId: string) => {
  if (!isSignedIn()) return loggedOut();

  try {
    const data = await CrateService.fetchCrate(crateId);
    const crate = CrateService.mapToOriginalCase(
      data as unknown as Record<string, any>,
    );
    const rawTracks: iTrack[] = Array.isArray(crate.Tracks)
      ? (crate.Tracks as iTrack[])
      : [];
    const tracks = rawTracks
      .slice(0, 80)
      .map((track, index) => summarizeCatalogTrack(track, index));

    return {
      available: true,
      source: 'crate-hackers',
      order: 'bpm-and-key',
      id: crate._id ?? crateId,
      title: crate.Title ?? 'Untitled crate',
      description: crate.Description ?? null,
      trackCount: rawTracks.length,
      tracks,
      note: 'Tracks are in the curated BPM and key order. Do not reshuffle them.',
    };
  } catch (error) {
    return {
      available: false,
      message:
        error instanceof Error
          ? error.message
          : 'Could not load that Crate Hackers crate.',
    };
  }
};

const MAX_MY_CRATE_TRACKS = 200;

export type { AgentCrateTrackInput };

const loadSourceTracks = async (
  crateId: string,
): Promise<{
  tracks: iTrack[];
  description?: string;
  sourceTitle?: string;
}> => {
  const data = await CrateService.fetchCrate(crateId);
  const crate = CrateService.mapToOriginalCase(
    data as unknown as Record<string, any>,
  );
  const tracks = Array.isArray(crate.Tracks) ? (crate.Tracks as iTrack[]) : [];
  const description =
    typeof crate.Description === 'string' ? crate.Description : undefined;
  const sourceTitle = typeof crate.Title === 'string' ? crate.Title : undefined;
  const hydrated = await hydrateCrateTracksMetadata(
    tracks.map((track) => track as iTrack),
  );
  return { tracks: ensureTrackIds(hydrated), description, sourceTitle };
};

export const createAgentMyCrate = async (args: {
  title: string;
  description?: string;
  sourceCrateId?: string;
  tracks?: AgentCrateTrackInput[];
  /** Magic Sort the crate into playable BPM and key order before saving. */
  mixableOrder?: boolean;
}) => {
  if (!isSignedIn()) return loggedOut();

  const title = args.title?.trim();
  if (!title) {
    return { available: false, message: 'title is required.' };
  }

  let tracks: iTrack[] = [];
  let description = args.description?.trim();
  let copiedFrom: { id: string; title: string } | undefined;

  try {
    if (args.sourceCrateId?.trim()) {
      const sourceId = args.sourceCrateId.trim();
      const source = await loadSourceTracks(sourceId);
      tracks = source.tracks;
      if (!description) description = source.description;
      if (source.sourceTitle) {
        copiedFrom = { id: sourceId, title: source.sourceTitle };
      }
    } else if (Array.isArray(args.tracks) && args.tracks.length > 0) {
      tracks = await resolveAgentTracksForCrate(args.tracks);
    } else {
      return {
        available: false,
        message:
          'Provide sourceCrateId to copy a catalog or example crate, or tracks with a title on each entry.',
      };
    }
  } catch (error) {
    return {
      available: false,
      message:
        error instanceof Error
          ? error.message
          : 'Could not load tracks for that crate.',
    };
  }

  if (!tracks.length) {
    return { available: false, message: 'No tracks to save.' };
  }
  if (tracks.length > MAX_MY_CRATE_TRACKS) {
    return {
      available: false,
      message: `That is ${tracks.length} tracks. My Crates hold at most ${MAX_MY_CRATE_TRACKS}.`,
    };
  }

  tracks = ensureTrackIds(await hydrateCrateTracksMetadata(tracks));

  // Same Magic Sort as the crate view button, run on the hydrated BPM/key data.
  const sortedForPlay = args.mixableOrder === true;
  if (sortedForPlay) {
    tracks = findBestMixableSequence(tracks);
  }

  const payload: iCrate = {
    _id: '',
    Title: title,
    Description: description || 'Created from Ask Crate Hackers',
    Tracks: tracks.map((track) => ({
      ID: track.SpotifyID || track.ID,
      SpotifyID: track.SpotifyID || track.ID,
      Title: track.Title,
      Artist: track.Artist,
      AlbumCover: track.AlbumCover,
      PreviewURL: track.PreviewURL,
      BPM: track.BPM,
      Key: track.Key,
      Key_Camelot: track.Key_Camelot,
      energy: track.energy,
      danceability: track.danceability,
      valence: track.valence,
      popularity: track.popularity,
    })),
    createdFrom: 'Agent',
  };

  try {
    const response = await CrateService.createMyCrate(payload);
    const crateId =
      typeof response?.id === 'string'
        ? response.id
        : typeof response?._id === 'string'
          ? response._id
          : '';
    if (!crateId) {
      return {
        available: false,
        message:
          typeof response?.message === 'string'
            ? response.message
            : 'Server did not return a crate id.',
      };
    }

    const mineLink = `ch://mine/${crateId}`;
    const url = `/my-crates/crate/${crateId}`;
    const catalogCopy = Boolean(copiedFrom);

    return {
      available: true,
      id: crateId,
      title,
      url,
      link: mineLink,
      trackCount: tracks.length,
      canvasOpened: false,
      copiedFromCatalog: catalogCopy,
      sourceCatalog: copiedFrom ?? null,
      order: 'bpm-and-key',
      sortedForPlay,
      note: catalogCopy
        ? 'Copied a catalog crate into My Crates. Reply with one short sentence and [title](ch://mine/ID) naming which catalog crate it came from — no track table and no further tools unless they asked for something else.'
        : sortedForPlay
          ? 'Saved the My Crate in playable BPM and key order. Reply with one short sentence and [title](ch://mine/ID) only — no track table, no recap of tracks, and no further tools unless they asked for export or something else explicitly.'
          : 'Saved the My Crate. Reply with one short sentence and [title](ch://mine/ID) only — no track table, no recap of tracks, and no further tools unless they asked for export or something else explicitly.',
    };
  } catch (error) {
    let message =
      error instanceof Error ? error.message : 'Could not create that crate.';
    if (message.includes('array length exceeds') || message.includes('200')) {
      message = `My Crates hold at most ${MAX_MY_CRATE_TRACKS} tracks.`;
    }
    return { available: false, message };
  }
};
